import type {
  EventPayload,
  ErrorUserData,
  TransactionUserData,
  PhaseUserData,
} from '@wix/panorama-common-shared/types';
import { LogLevel } from '@wix/panorama-common-shared/types';
import type { PanoramaClientForComponent } from '../panorama-client-component';
import type { Breadcrumb, ErrorData, ErrorMonitorOptions } from '../types';

export enum Hook {
  afterCreateClientForComponent,
  beforeReport,
  beforeReportTransactionStart,
  afterReportTransactionStart,
  beforeReportTransactionFinish,
  afterReportTransactionFinish,
  beforeReportPhaseStart,
  afterReportPhaseStart,
  beforeReportPhaseFinish,
  afterReportPhaseFinish,
  beforeReportError,
  afterReportError,
  beforeAddBreadcrumb,
  afterAddBreadcrumb,
  beforeReportLog,
  afterReportLog,
  beforeUnhandledError,
}

type Subscriber<T extends any[]> = (...args: T) => false | void;

type ArgsByHook = {
  [Hook.afterCreateClientForComponent]: [client: PanoramaClientForComponent];
  [Hook.beforeReport]: [eventPayload: EventPayload];
  [Hook.beforeReportTransactionStart]: [
    transactionName: string,
    data?: TransactionUserData,
  ];
  [Hook.beforeReportTransactionFinish]: [
    transactionName: string,
    data?: TransactionUserData,
  ];
  [Hook.afterReportTransactionStart]: [
    transactionName: string,
    eventPayload: EventPayload,
  ];
  [Hook.afterReportTransactionFinish]: [
    transactionName: string,
    eventPayload: EventPayload,
  ];
  [Hook.beforeReportPhaseStart]: [
    transactionName: string,
    data?: PhaseUserData,
  ];
  [Hook.beforeReportPhaseFinish]: [
    transactionName: string,
    data?: PhaseUserData,
  ];
  [Hook.afterReportPhaseStart]: [
    transactionName: string,
    eventPayload: EventPayload,
  ];
  [Hook.afterReportPhaseFinish]: [
    transactionName: string,
    eventPayload: EventPayload,
  ];
  [Hook.beforeReportError]: [
    error: Error,
    data?: ErrorData & ErrorUserData,
    errorMonitorOptions?: ErrorMonitorOptions,
  ];
  [Hook.afterReportError]: [
    error: Error,
    data?: ErrorData & ErrorUserData,
    errorMonitorOptions?: ErrorMonitorOptions,
  ];
  [Hook.beforeAddBreadcrumb]: [Breadcrumb];
  [Hook.afterAddBreadcrumb]: [Breadcrumb];
  [Hook.beforeReportLog]: [
    logLevel: LogLevel,
    message: string,
    data?: Record<string, any>,
  ];
  [Hook.afterReportLog]: [
    logLevel: LogLevel,
    message: string,
    data?: Record<string, any>,
  ];
  [Hook.beforeUnhandledError]: [error?: Error];
};

type SubscribersByHook = {
  [P in Hook]?: Subscriber<ArgsByHook[P]>[];
};

export type SubscriberByHook = {
  [P in Hook]: Subscriber<ArgsByHook[P]>;
};

export class HooksManager {
  private readonly subscribers: SubscribersByHook = {};

  tap<T extends Hook>(hook: T, subscriber: Subscriber<ArgsByHook[T]>) {
    this.subscribers[hook] = this.subscribers[hook] || [];
    this.subscribers[hook]!.push(subscriber);
  }

  invoke<T extends Hook>(hook: T, ...args: ArgsByHook[T]): boolean {
    const subscribers = this.subscribers[hook];

    if (!subscribers) {
      return true;
    }

    for (const subscriber of subscribers) {
      if (subscriber(...args) === false) {
        return false;
      }
    }

    return true;
  }
}
